import { useEffect, useRef, useState } from 'react';
import {
  FormControl,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Link,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  useApi,
  configApiRef,
  fetchApiRef,
  githubAuthApiRef,
} from '@backstage/core-plugin-api';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';

/**
 * Custom scaffolder field that conditionally renders team information fields
 * based on whether a GitHub repository already exists for the selected Entra group.
 *
 * When a repo exists:
 * - Shows an informational alert and links to the existing repo
 * - Hides the description / team-slug fields and sets the repoExists flag
 *
 * When the repo doesn't exist:
 * - Shows the team information form fields
 * - Acquires the user's GitHub OAuth token and verifies active univerus org
 *   membership, storing the token as a template secret so `publish:github`
 *   creates the repository under the user's account. If the user has no GitHub
 *   account or org access, the form blocks with a plain-language message.
 */
export const TeamInformationFields = ({
  formData,
  onChange,
  formContext,
}: any) => {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const githubAuth = useApi(githubAuthApiRef);
  const { setSecrets } = useTemplateSecrets();
  const [repoExists, setRepoExists] = useState<boolean>(false);
  const [repoUrl, setRepoUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [githubUser, setGithubUser] = useState<string | null>(null);
  const [githubAuthError, setGithubAuthError] = useState<string | null>(null);
  // Ref to the latest form data so the async verification effect merges into
  // current state instead of clobbering fields written by the repo-check effect.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Get the selected Entra group from form context
  const selectedGroup = formContext.formData?.entraGroupRef;

  useEffect(() => {
    // Guards the async repo check below: a response that lands after the
    // selected group changed (or the component unmounted) is dropped instead
    // of overwriting state for the newer selection.
    let cancelled = false;
    const checkRepoExists = async () => {
      if (!selectedGroup) {
        setRepoExists(false);
        setRepoUrl('');
        onChange({
          ...formDataRef.current,
          _repoExists: false,
          _existingRepoUrl: undefined,
          _groupDisplayName: undefined,
          _graphGroupId: undefined,
          _groupEntityName: undefined,
          _domainName: undefined,
          _repoName: undefined,
          githubRepoUrl: undefined,
          _githubVerified: undefined,
        });
        return;
      }

      // Extract group name from entity ref (format: group:default/group-name)
      const groupName = selectedGroup.split('/')[1];
      if (!groupName) return;

      setLoading(true);
      setError('');

      try {
        const backendUrl = configApi.getString('backend.baseUrl');
        const response = await fetchApi.fetch(
          `${backendUrl}/api/unlinked-entra-groups/check-repo-exists?groupName=${encodeURIComponent(
            groupName,
          )}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        if (!data.graphGroupId) {
          throw new Error('Received Graph response without group identifier');
        }
        if (cancelled) return;
        setRepoExists(data.exists);
        // The backend derives the repo name from the catalog: {bu}-team when
        // the group owns a Domain (via its bu-code annotation), team-{name}
        // otherwise. The frontend never re-derives it.
        if (data.exists) {
          setRepoUrl(data.repoUrl);
          // Only set metadata fields when repo EXISTS - they're optional so won't show when undefined.
          // Merge from the ref, not the formData prop: the prop is stale inside
          // this async callback and would clobber edits made while the request
          // was in flight.
          onChange({
            ...formDataRef.current,
            _repoExists: true,
            _existingRepoUrl: data.repoUrl,
            _groupDisplayName: data.groupDisplayName,
            _graphGroupId: data.graphGroupId,
            _groupEntityName: data.groupEntityName,
            _domainName: data.domainName || undefined,
            _repoName: data.repoName,
            githubRepoUrl: data.repoUrl,
            // Existing repo: nothing is published, so no token/verification is
            // needed and submission is allowed.
            _githubVerified: true,
          });
        } else {
          // Don't set _repoExists or _existingRepoUrl when repo doesn't exist
          onChange({
            ...formDataRef.current,
            _groupDisplayName: data.groupDisplayName,
            _graphGroupId: data.graphGroupId,
            _groupEntityName: data.groupEntityName,
            _domainName: data.domainName || undefined,
            _repoName: data.repoName,
            githubRepoUrl: `https://github.com/univerus/${data.repoName}`,
            // Cleared until GitHub auth + org membership is verified below; the
            // required _githubVerified field blocks submission until then.
            _githubVerified: undefined,
          });
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Failed to check repo existence:', err);
        setError(String(err));
      } finally {
        // A newer run owns the loading flag once this one is cancelled.
        if (!cancelled) setLoading(false);
      }
    };

    checkRepoExists();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, configApi, fetchApi]); // Note: formData/onChange excluded from deps to prevent infinite loop; async callbacks merge from formDataRef

  // Acquire the user's GitHub OAuth token and verify active univerus org
  // membership, then stash the token so the publish:github step creates the
  // repository under the user's account. If GitHub can't be reached or the user
  // isn't an active org member, the form blocks with a plain-language message.
  useEffect(() => {
    if (loading || repoExists || !selectedGroup) return undefined;

    let cancelled = false;

    const acquireToken = async () => {
      try {
        setGithubAuthError(null);
        const token = await githubAuth.getAccessToken(['read:org', 'repo']);

        // Fetch the authenticated user's login for display
        const res = await fetch('https://api.github.com/user', {
          headers: {
            Authorization: `token ${token}`,
            Accept: 'application/vnd.github+json',
          },
        });
        if (!res.ok) {
          throw new Error(`GitHub user lookup failed (HTTP ${res.status})`);
        }
        const user = await res.json();

        // Confirm active univerus org membership before allowing creation
        const memberRes = await fetch(
          'https://api.github.com/user/memberships/orgs/univerus',
          {
            headers: {
              Authorization: `token ${token}`,
              Accept: 'application/vnd.github+json',
            },
          },
        );
        // A 404 (or a 200 with a non-active state) is a definitive "not an
        // active member" answer. Any other non-2xx (403 rate limit, 5xx,
        // network) is a transient verification failure, not a membership verdict.
        if (memberRes.status === 404) {
          throw new Error('not-a-member');
        }
        if (!memberRes.ok) {
          throw new Error('verify-failed');
        }
        const membership = await memberRes.json();
        if (membership.state !== 'active') {
          throw new Error('not-a-member');
        }

        if (!cancelled) {
          setSecrets({ USER_OAUTH_TOKEN: token });
          setGithubUser(user.login);
          onChange({ ...formDataRef.current, _githubVerified: true });
        }
      } catch (err) {
        if (!cancelled) {
          const reason = err instanceof Error ? err.message : 'verify-failed';
          setGithubAuthError(
            reason === 'not-a-member'
              ? 'You need a GitHub account that belongs to the Univerus organization to create a team repository, and yours is not connected. Ask a developer on your team, or contact #hub-support, and they can create it for you.'
              : 'We could not verify your GitHub access right now (this can be a temporary GitHub issue). Please try again in a moment; if it keeps happening, contact #hub-support.',
          );
          onChange({ ...formDataRef.current, _githubVerified: undefined });
        }
      }
    };

    acquireToken();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, repoExists, selectedGroup, githubAuth, setSecrets]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" my={2}>
        <CircularProgress size={20} />
        <Box ml={2}>
          <Typography>Checking if repository already exists...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" style={{ marginTop: 16, marginBottom: 16 }}>
        <AlertTitle>Error Checking Repository</AlertTitle>
        Failed to check if repository exists: {error}
      </Alert>
    );
  }

  if (repoExists) {
    return (
      <Alert severity="info" style={{ marginTop: 16, marginBottom: 16 }}>
        <AlertTitle>Repository Already Exists</AlertTitle>
        <Typography variant="body2" paragraph>
          A GitHub repository for this team already exists at{' '}
          <Link href={repoUrl} target="_blank" rel="noopener noreferrer">
            {repoUrl}
          </Link>
        </Typography>
        <Typography variant="body2" paragraph>
          This scaffolder will create the team entity in Backstage and link it
          to the existing repository. Since the repository already has a
          description and configuration, you won't need to provide those
          details.
        </Typography>
        <Typography variant="body2">
          <strong>What happens next:</strong>
        </Typography>
        <ul style={{ marginTop: 8 }}>
          <li>The team group entity will be created in Backstage's catalog</li>
          <li>The entity will be linked to the existing GitHub repository</li>
          <li>Team members will gain access to the repository in Backstage</li>
          <li>No changes will be made to the existing repository</li>
        </ul>
      </Alert>
    );
  }

  // Repository doesn't exist - show normal form fields
  return (
    <Box>
      {githubAuthError && (
        <Alert severity="warning" style={{ marginTop: 16, marginBottom: 16 }}>
          <AlertTitle>GitHub account needed</AlertTitle>
          <Typography variant="body2">{githubAuthError}</Typography>
        </Alert>
      )}

      {githubUser && (
        <Alert severity="success" style={{ marginTop: 16, marginBottom: 16 }}>
          <AlertTitle>Repository will be created by @{githubUser}</AlertTitle>
          <Typography variant="body2">
            This repository will be created in the <strong>univerus</strong>{' '}
            organization using your GitHub account. The initial commit will be
            attributed to you.
          </Typography>
        </Alert>
      )}

      <FormControl fullWidth margin="normal" required>
        <TextField
          label="Team Description"
          value={formData?.teamDescription || ''}
          onChange={e => {
            const {
              repoExists: _old1,
              existingRepoUrl: _old2,
              ...cleanFormData
            } = formData;
            onChange({ ...cleanFormData, teamDescription: e.target.value });
          }}
          multiline
          rows={3}
          required
          helperText="Brief description of the team's purpose and responsibilities"
          variant="outlined"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <TextField
          label="Team Email (Optional)"
          value={formData?.teamEmail || ''}
          onChange={e => {
            const {
              repoExists: _old1,
              existingRepoUrl: _old2,
              ...cleanFormData
            } = formData;
            onChange({ ...cleanFormData, teamEmail: e.target.value });
          }}
          type="email"
          helperText="Team contact email address"
          variant="outlined"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <TextField
          label="GitHub Team Slug (Optional)"
          value={formData?.githubTeamSlug || ''}
          onChange={e => {
            const {
              repoExists: _old1,
              existingRepoUrl: _old2,
              ...cleanFormData
            } = formData;
            onChange({ ...cleanFormData, githubTeamSlug: e.target.value });
          }}
          helperText="GitHub team slug for CODEOWNERS. Leave empty if team doesn't have a GitHub team."
          variant="outlined"
          inputProps={{
            pattern: '^[a-z0-9-]*$',
          }}
        />
      </FormControl>
    </Box>
  );
};
